import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import config from '../../config';
import { useSiteContent, mediaUrl } from '../../lib/siteContent';
import { tickerOffset } from './TickerBanner';

const HERO_IMAGES = [
  '/hero/family-1.jpg',
  '/hero/family-2.png',
];

const WhatsAppIcon = () => (
  <svg className="w-3.5 h-3.5 opacity-70" viewBox="0 0 24 24" fill="#25D366" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function HeroSection() {
  const site = useSiteContent();
  const hero = site.hero || {};
  const heroImages = (hero.images && hero.images.length ? hero.images : HERO_IMAGES).map(mediaUrl);
  const heroVideo = mediaUrl(hero.video_url);
  const [widgetStep, setWidgetStep] = useState(0);
  const [heroSlide, setHeroSlide] = useState(0);
  const widgetSteps = [
    { label: 'Consultation Confirmed', accent: '#22c55e' },
    { label: 'Nurse En Route', accent: '#f37721' },
    { label: 'Vaccination Complete', accent: '#673c96' },
  ];

  useEffect(() => {
    const timer = setInterval(() => setWidgetStep(p => (p + 1) % widgetSteps.length), 3500);
    return () => clearInterval(timer);
  }, [widgetSteps.length]);

  useEffect(() => {
    const timer = setInterval(() => setHeroSlide(p => (p + 1) % Math.max(heroImages.length, 1)), 2000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  const current = widgetSteps[widgetStep];

  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-white" style={{ paddingTop: `${100 + tickerOffset(site)}px`, paddingBottom: '0px' }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full bg-[#f37721]/[0.03] blur-3xl" />
        <div className="absolute -bottom-60 -left-40 w-[600px] h-[600px] rounded-full bg-[#673c96]/[0.03] blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6 lg:gap-8 items-center">

          {/* ── Left content ── */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 bg-[#673c96]/8 text-[#673c96] rounded-full px-4 py-1.5 text-xs font-semibold mb-6 tracking-widest uppercase"
              style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <Shield className="w-3.5 h-3.5" />
              {hero.eyebrow || "Trusted by Families in Your Neighbourhood"}
            </div>

            <h1
              className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-[#311b52] leading-[1.2] mb-5"
              style={{ fontFamily: 'Poppins, sans-serif' }}
              data-testid="hero-headline"
            >
              {(hero.headline || "Premium At-Home Vaccination for Your Family").split(hero.headline_accent || "Vaccination").map((part, i, arr) => (
                <span key={i}>
                  {part}
                  {i < arr.length - 1 && <span className="text-[#f37721]">{hero.headline_accent || "Vaccination"}</span>}
                </span>
              ))}
            </h1>

            <p className="text-sm md:text-base text-[#3e195e]/80 leading-relaxed mb-4 max-w-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {hero.subhead || "Certified nurses. Strict hygiene protocols. Long-term digital vaccination records. Book for yourself, your children, and your parents — all from the comfort of home."}
            </p>
            <p
              data-testid="hero-positioning-line"
              className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#f37721] mb-8"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {hero.positioning || "Premium in experience. Sensible in access."}
            </p>

            <div className="flex flex-col items-stretch gap-3 mb-6 w-full max-w-lg">
              <Link
                to="/vaccination-catalogue"
                data-testid="hero-request-btn"
                className="bmj-cta-thump-b group inline-flex w-full items-center justify-center gap-2 text-white rounded-full px-8 py-3.5 font-semibold text-base"
                style={{
                  backgroundColor: '#e56e1e',
                  boxShadow: '0 8px 20px rgba(243,119,33,0.28)',
                  transition: 'box-shadow 200ms ease-out, background-color 200ms ease-out',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.animationPlayState = 'paused';
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(243,119,33,0.35)';
                  e.currentTarget.style.backgroundColor = '#d9651a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = '';
                  e.currentTarget.style.animationPlayState = 'running';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(243,119,33,0.28)';
                  e.currentTarget.style.backgroundColor = '#e56e1e';
                }}
              >
                Book now
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-[3px]" />
              </Link>
              <a
                href={config.getWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#673c96]/55 hover:text-[#673c96] transition-colors"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                <WhatsAppIcon />
                Book on WhatsApp
              </a>
            </div>

            {/* Trust badges row — single consolidated row */}
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 md:gap-6 text-xs text-[#673c96]/80 tracking-wide uppercase mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#22c55e]" />Certified Nurses</div>
              <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#22c55e]" />Strict hygiene protocols</div>
              <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#22c55e]" />Long-term digital records</div>
            </div>
          </motion.div>

          {/* ── Mobile-only image ── */}
          <div className="lg:hidden mt-8">
            <div className="rounded-2xl overflow-hidden shadow-xl relative" style={{ aspectRatio: '4/3' }}>
              {heroVideo ? (
                <video src={heroVideo} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline />
              ) : heroImages.map((src, i) => (
                <img key={i} src={src} alt="BookMyJab family"
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                  style={{ opacity: heroSlide === i ? 1 : 0 }} />
              ))}
            </div>
          </div>

          {/* ── Right: Family image — desktop only at large size ── */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            {/* Tickers ABOVE image — never cover the photo */}
            <div className="flex items-center justify-between mb-3 px-1">
              <motion.div
                key={widgetStep}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="inline-flex items-center gap-2 bg-white rounded-full shadow-md px-4 py-2 border border-[#673c96]/10"
              >
                <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: current.accent }} />
                <span className="text-xs font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>{current.label}</span>
              </motion.div>

              <div className="inline-flex items-center gap-2 bg-white rounded-full shadow-md px-4 py-2 border border-[#673c96]/10">
                <div className="flex -space-x-1.5">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="w-5 h-5 rounded-full bg-gradient-to-br from-[#673c96] to-[#f37721] border-[1.5px] border-white flex items-center justify-center">
                      <span className="text-[6px] font-bold text-white">{i}</span>
                    </div>
                  ))}
                </div>
                <span className="text-xs font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>3 nurses ready</span>
              </div>
            </div>

            {/* Image — 2-second crossfade loop */}
            <div className="rounded-[2rem] overflow-hidden shadow-2xl relative" style={{ aspectRatio: '4/3' }}>
              {heroVideo ? (
                <video src={heroVideo} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline />
              ) : heroImages.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt="BookMyJab family"
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                  style={{ opacity: heroSlide === i ? 1 : 0 }}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Floating WhatsApp is now mounted site-wide at the root (App.js) */}
    </section>
  );
}
